import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, ClockCounterClockwise, ShieldStar, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PendingProposals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/edit-proposals", { params: { status: filter } });
      setItems(data || []);
    } catch {
      toast.error("Failed to load proposals");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (p) => {
    if (!window.confirm(`Approve and apply this edit to ${p.lender_name}?`)) return;
    try {
      await api.post(`/edit-proposals/${p.id}/approve`);
      toast.success("Approved and applied");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const reject = async (p) => {
    if (!window.confirm("Reject this proposal?")) return;
    try {
      await api.post(`/edit-proposals/${p.id}/reject`);
      toast.success("Rejected");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };
  const cancel = async (p) => {
    if (!window.confirm("Cancel your proposal?")) return;
    try {
      await api.post(`/edit-proposals/${p.id}/cancel`);
      toast.success("Cancelled");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const fmtValue = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-slate-400 italic">empty</span>;
    if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="text-slate-400 italic">empty</span>;
    if (typeof v === "object") return <code className="text-[10px]">{JSON.stringify(v)}</code>;
    return String(v);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Governance</div>
        <h1 className="font-heading text-4xl font-bold tracking-tighter">Pending edits</h1>
        <p className="text-slate-600 mt-1">
          This directory is self-governing. Any teammate can propose an edit; <strong>one other person</strong> must approve before the change goes live.
        </p>
      </div>

      <div className="flex items-center gap-2">
        {["pending", "approved", "rejected", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.1em] font-semibold rounded border transition-colors ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            {s}
          </button>
        ))}
        <button onClick={reload} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {loading && <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
          No {filter} proposals.
        </div>
      )}

      <div className="space-y-4">
        {items.map((p) => {
          const isOwn = p.proposer_id === user?.id;
          return (
            <div key={p.id} data-testid={`proposal-${p.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <Link to={`/lenders/${p.lender_id}`} className="font-heading text-lg font-bold text-slate-900 hover:text-blue-600">
                    {p.lender_name}
                  </Link>
                  <div className="text-xs text-slate-500 mt-1">
                    Proposed by <strong>{p.proposer_name || p.proposer_email}</strong>
                    {isOwn && <span className="ml-1 text-blue-600 font-semibold">(you)</span>}
                    {" · "}{new Date(p.created_at).toLocaleString()}
                  </div>
                  {p.reason && <div className="text-xs text-slate-700 mt-2 italic">"{p.reason}"</div>}
                </div>
                <div className="flex items-center gap-2">
                  {p.status === "pending" && !isOwn && (
                    <>
                      <Button size="sm" onClick={() => approve(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${p.id}`}>
                        <CheckCircle size={14} weight="fill" className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(p)} data-testid={`reject-${p.id}`}>
                        <XCircle size={14} className="mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {p.status === "pending" && isOwn && (
                    <Button size="sm" variant="outline" onClick={() => cancel(p)} data-testid={`cancel-${p.id}`}>
                      Cancel
                    </Button>
                  )}
                  {p.status === "approved" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle size={11} weight="fill" /> Approved
                      {p.approver_emails?.[0] && <span className="font-normal lowercase tracking-normal ml-1">by {p.approver_emails[0]}</span>}
                    </span>
                  )}
                  {p.status === "rejected" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">
                      <XCircle size={11} weight="fill" /> Rejected
                      {p.rejecter_email && <span className="font-normal lowercase tracking-normal ml-1">by {p.rejecter_email}</span>}
                    </span>
                  )}
                  {p.status === "cancelled" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded bg-slate-100 text-slate-600 border border-slate-200">
                      <ClockCounterClockwise size={11} /> Cancelled
                    </span>
                  )}
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">
                  {Object.keys(p.changes).length} field{Object.keys(p.changes).length !== 1 ? "s" : ""} to change
                </div>
                <div className="space-y-2">
                  {Object.entries(p.changes).map(([field, newVal]) => (
                    <div key={field} className="grid grid-cols-12 gap-3 items-start text-sm border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
                      <div className="col-span-3 text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">{field}</div>
                      <div className="col-span-9 grid grid-cols-2 gap-3">
                        <div className="bg-red-50 border border-red-100 rounded px-2 py-1 text-xs text-red-900 line-through opacity-80">
                          {fmtValue(p.before?.[field])}
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 rounded px-2 py-1 text-xs text-emerald-900">
                          {fmtValue(newVal)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
